import _defineProperty from "/app/workspaces/v_1/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// @ts-check
import { CancelError, uploadFile } from '@uploadcare/upload-client';
import { shrinkFile } from '@uploadcare/image-shrink';
import { ActivityBlock } from '../../abstract/ActivityBlock.js';
import { UploaderBlock } from '../../abstract/UploaderBlock.js';
import { createCdnUrl, createCdnUrlModifiers, createOriginalUrl } from '../../utils/cdn-utils.js';
import { fileCssBg } from '../svg-backgrounds/svg-backgrounds.js';
import { debounce } from '../utils/debounce.js';
import { generateThumb } from '../utils/resizeImage.js';
import { parseShrink } from '../../utils/parseShrink.js';
const FileItemState = Object.freeze({
  FINISHED: Symbol(0),
  FAILED: Symbol(1),
  UPLOADING: Symbol(2),
  IDLE: Symbol(3)
});
export class FileItem extends UploaderBlock {
  constructor() {
    super();
    _defineProperty(this, "couldBeCtxOwner", true);
    _defineProperty(this, "pauseRender", true);
    /** @private */
    _defineProperty(this, "_entrySubs", new Set());
    /**
     * @private
     * @type {any} TODO: Add types for upload entry
     */
    _defineProperty(this, "_entry", null);
    /** @private */
    _defineProperty(this, "_isIntersecting", false);
    /** @private */
    _defineProperty(this, "_debouncedGenerateThumb", debounce(this._generateThumbnail.bind(this), 100));
    /** @private */
    _defineProperty(this, "_debouncedCalculateState", debounce(this._calculateState.bind(this), 100));
    /** @private */
    _defineProperty(this, "_renderedOnce", false);
    this.init$ = _objectSpread(_objectSpread({}, this.init$), {}, {
      uid: '',
      itemName: '',
      errorText: '',
      thumbUrl: '',
      progressValue: 0,
      progressVisible: false,
      badgeIcon: '',
      isFinished: false,
      isFailed: false,
      isUploading: false,
      isFocused: false,
      isEditable: false,
      state: FileItemState.IDLE,
      onEdit: () => {
        this.set$({
          '*focusedEntry': this._entry
        });
        if (this.hasBlockInCtx(b => b.activityType === ActivityBlock.activities.DETAILS)) {
          this.$['*currentActivity'] = ActivityBlock.activities.DETAILS;
        } else {
          this.$['*currentActivity'] = ActivityBlock.activities.CLOUD_IMG_EDIT;
        }
      },
      onRemove: () => {
        this.uploadCollection.remove(this.$.uid);
      },
      onUpload: () => {
        this.upload();
      }
    });
  }
  _reset() {
    for (let sub of this._entrySubs) {
      sub.remove();
    }
    this._debouncedGenerateThumb.cancel();
    this._debouncedCalculateState.cancel();
    this._entrySubs = new Set();
    this._entry = null;
  }

  /**
   * @private
   * @param {IntersectionObserverEntry[]} entries
   */
  _observerCallback(entries) {
    let [entry] = entries;
    this._isIntersecting = entry.isIntersecting;
    if (entry.isIntersecting && !this._renderedOnce) {
      this.render();
      this._renderedOnce = true;
    }
    if (entry.intersectionRatio === 0) {
      this._debouncedGenerateThumb.cancel();
    } else {
      this._debouncedGenerateThumb();
    }
  }

  /** @private */
  _calculateState() {
    if (!this._entry) {
      return;
    }
    let entry = this._entry;
    let state = FileItemState.IDLE;
    if (entry.getValue('errors').length > 0) {
      state = FileItemState.FAILED;
    } else if (entry.getValue('isUploading')) {
      state = FileItemState.UPLOADING;
    } else if (entry.getValue('fileInfo')) {
      state = FileItemState.FINISHED;
    }
    this.$.state = state;
  }

  /** @private */
  async _generateThumbnail() {
    var _entry$getValue;
    if (!this._entry) {
      return;
    }
    let entry = this._entry;
    if (entry.getValue('fileInfo') && entry.getValue('isImage')) {
      let size = this.cfg.thumbSize;
      let thumbUrl = this.proxyUrl(createCdnUrl(createOriginalUrl(this.cfg.cdnCname, this._entry.getValue('uuid')), createCdnUrlModifiers(entry.getValue('cdnUrlModifiers'), "scale_crop/".concat(size, "x").concat(size, "/center"))));
      let currentThumbUrl = entry.getValue('thumbUrl');
      if (currentThumbUrl !== thumbUrl) {
        entry.setValue('thumbUrl', thumbUrl);
        (currentThumbUrl === null || currentThumbUrl === void 0 ? void 0 : currentThumbUrl.startsWith('blob:')) && URL.revokeObjectURL(currentThumbUrl);
      }
      return;
    }
    if (entry.getValue('thumbUrl')) {
      return;
    }
    if ((_entry$getValue = entry.getValue('file')) !== null && _entry$getValue !== void 0 && _entry$getValue.type.includes('image')) {
      try {
        let thumbUrl = await generateThumb(entry.getValue('file'), this.cfg.thumbSize);
        entry.setValue('thumbUrl', thumbUrl);
      } catch (err) {
        let color = window.getComputedStyle(this).getPropertyValue('--clr-generic-file-icon');
        entry.setValue('thumbUrl', fileCssBg(color));
      }
    } else {
      let color = window.getComputedStyle(this).getPropertyValue('--clr-generic-file-icon');
      entry.setValue('thumbUrl', fileCssBg(color));
    }
  }

  /**
   * @private
   * @param {string} prop
   * @param {(value: any) => void} handler
   */
  _subEntry(prop, handler) {
    let sub = this._entry.subscribe(prop, /** @param {any} value */value => {
      if (this.isConnected) {
        handler(value);
      }
    });
    this._entrySubs.add(sub);
  }

  /**
   * @private
   * @param {String} id
   */
  _handleEntryId(id) {
    var _this$uploadCollectio;
    this._reset();

    /** @type {import('../../abstract/TypedData.js').TypedData} */
    let entry = (_this$uploadCollectio = this.uploadCollection) === null || _this$uploadCollectio === void 0 ? void 0 : _this$uploadCollectio.read(id);
    this._entry = entry;
    if (!entry) {
      return;
    }
    this._subEntry('uploadProgress', uploadProgress => {
      this.$.progressValue = uploadProgress;
    });
    this._subEntry('fileName', name => {
      this.$.itemName = name || entry.getValue('externalUrl') || this.l10n('file-no-name');
      this._debouncedCalculateState();
    });
    this._subEntry('externalUrl', externalUrl => {
      this.$.itemName = entry.getValue('fileName') || externalUrl || this.l10n('file-no-name');
    });
    this._subEntry('fileInfo', fileInfo => {
      this._debouncedCalculateState();
      if (fileInfo && this._isIntersecting) {
        this._debouncedGenerateThumb();
      }
    });
    this._subEntry('cdnUrlModifiers', () => {
      if (this._isIntersecting) {
        this._debouncedGenerateThumb();
      }
    });
    this._subEntry('thumbUrl', thumbUrl => {
      this.$.thumbUrl = thumbUrl ? "url(".concat(thumbUrl, ")") : '';
    });
    this._subEntry('errors', () => this._debouncedCalculateState());
    this._subEntry('isUploading', () => this._debouncedCalculateState());
    this._subEntry('fileSize', () => this._debouncedCalculateState());
    this._subEntry('mimeType', () => this._debouncedCalculateState());
    this._subEntry('isImage', () => this._debouncedCalculateState());
    if (this._isIntersecting) {
      this._debouncedGenerateThumb();
    }
  }
  initCallback() {
    super.initCallback();
    this.sub('uid', uid => {
      this._handleEntryId(uid);
    });
    this.sub('state', state => {
      this._handleState(state);
    });
    this.subConfigValue('useCloudImageEditor', () => this._debouncedCalculateState());
    this.onclick = () => {
      FileItem.activeInstances.forEach(inst => {
        if (inst === this) {
          inst.setAttribute('focused', '');
        } else {
          inst.removeAttribute('focused');
        }
      });
    };
    this.sub('*uploadTrigger', /** @param {Set<string>} itemsToUpload */
    itemsToUpload => {
      if (!itemsToUpload.has(this._entry.uid)) {
        return;
      }
      setTimeout(() => this.isConnected && this.upload());
    });
    FileItem.activeInstances.add(this);
  }

  /** @param {(typeof FileItemState)[keyof typeof FileItemState]} state */
  _handleState(state) {
    var _this$_entry, _this$_entry2, _this$_entry$getValue;
    if (state === FileItemState.FAILED) {
      this.$.badgeIcon = 'badge-error';
    } else if (state === FileItemState.FINISHED) {
      this.$.badgeIcon = 'badge-success';
    }
    if (state === FileItemState.UPLOADING) {
      this.$.isFocused = false;
    } else {
      this.$.progressValue = 0;
    }
    this.set$({
      isFailed: state === FileItemState.FAILED,
      isUploading: state === FileItemState.UPLOADING,
      isFinished: state === FileItemState.FINISHED,
      progressVisible: state === FileItemState.UPLOADING,
      isEditable: this.cfg.useCloudImageEditor && ((_this$_entry = this._entry) === null || _this$_entry === void 0 ? void 0 : _this$_entry.getValue('isImage')) && ((_this$_entry2 = this._entry) === null || _this$_entry2 === void 0 ? void 0 : _this$_entry2.getValue('cdnUrl')),
      errorText: (_this$_entry$getValue = this._entry.getValue('errors')) === null || _this$_entry$getValue === void 0 || (_this$_entry$getValue = _this$_entry$getValue[0]) === null || _this$_entry$getValue === void 0 ? void 0 : _this$_entry$getValue.message
    });
  }
  destroyCallback() {
    super.destroyCallback();
    FileItem.activeInstances.delete(this);
    this._reset();
  }
  connectedCallback() {
    super.connectedCallback();

    /** @private */
    this._observer = new window.IntersectionObserver(this._observerCallback.bind(this), {
      root: this.parentElement,
      rootMargin: '50% 0px 50% 0px',
      threshold: [0, 1]
    });
    this._observer.observe(this);
  }
  disconnectedCallback() {
    var _this$_observer;
    super.disconnectedCallback();
    this._debouncedGenerateThumb.cancel();
    (_this$_observer = this._observer) === null || _this$_observer === void 0 || _this$_observer.disconnect();
  }
  _settingsOfShrink() {
    return parseShrink(this.cfg.imageShrink);
  }

  /**
   * @private
   * @param {File} file
   */
  _processShrink(file) {
    return shrinkFile(file, this._settingsOfShrink());
  }
  async upload() {
    let entry = this._entry;
    if (!this.uploadCollection.read(entry.uid)) {
      return;
    }
    if (entry.getValue('fileInfo') || entry.getValue('isUploading') || entry.getValue('errors').length > 0) {
      return;
    }
    const multipleMax = this.cfg.multiple ? this.cfg.multipleMax : 1;
    if (multipleMax && this.uploadCollection.size > multipleMax) {
      return;
    }
    this._debouncedCalculateState();
    entry.setValue('isUploading', true);
    entry.setValue('errors', []);
    try {
      var _fileInfo$contentInfo, _fileInfo$contentInfo2, _entry$getValue2, _entry$getValue3;
      let abortController = new AbortController();
      entry.setValue('abortController', abortController);
      const uploadTask = async () => {
        let file = entry.getValue('file');
        if (file && this.cfg.imageShrink) {
          file = await this._processShrink(file).catch(() => file);
        }
        const fileInput = file || entry.getValue('externalUrl') || entry.getValue('uuid');
        const baseUploadClientOptions = this.getUploadClientOptions();
        /** @type {import('@uploadcare/upload-client').FileFromOptions} */
        const uploadClientOptions = _objectSpread(_objectSpread({}, baseUploadClientOptions), {}, {
          fileName: entry.getValue('fileName'),
          source: entry.getValue('source'),
          onProgress: progress => {
            if (progress.isComputable) {
              let percentage = progress.value * 100;
              entry.setValue('uploadProgress', percentage);
            }
          },
          signal: abortController.signal,
          metadata: await this.getMetadataFor(entry.uid)
        });
        this.debugPrint('upload options', fileInput, uploadClientOptions);
        return uploadFile(fileInput, uploadClientOptions);
      };

      /** @type {import('@uploadcare/upload-client').UploadcareFile} */
      let fileInfo = await this.$['*uploadQueue'].add(uploadTask);
      entry.setMultipleValues({
        fileInfo,
        isUploading: false,
        fileName: fileInfo.originalFilename,
        fileSize: fileInfo.size,
        isImage: fileInfo.isImage,
        mimeType: (_fileInfo$contentInfo = (_fileInfo$contentInfo2 = fileInfo.contentInfo) === null || _fileInfo$contentInfo2 === void 0 || (_fileInfo$contentInfo2 = _fileInfo$contentInfo2.mime) === null || _fileInfo$contentInfo2 === void 0 ? void 0 : _fileInfo$contentInfo2.mime) !== null && _fileInfo$contentInfo !== void 0 ? _fileInfo$contentInfo : fileInfo.mimeType,
        uuid: fileInfo.uuid,
        cdnUrl: (_entry$getValue2 = entry.getValue('cdnUrl')) !== null && _entry$getValue2 !== void 0 ? _entry$getValue2 : fileInfo.cdnUrl,
        cdnUrlModifiers: (_entry$getValue3 = entry.getValue('cdnUrlModifiers')) !== null && _entry$getValue3 !== void 0 ? _entry$getValue3 : '',
        uploadProgress: 100
      });
      if (entry === this._entry) {
        this._debouncedCalculateState();
      }
    } catch (cause) {
      if (cause instanceof CancelError && cause.isCancel) {
        entry.setMultipleValues({
          isUploading: false,
          uploadProgress: 0
        });
      } else {
        entry.setMultipleValues({
          isUploading: false,
          uploadProgress: 0,
          uploadError: cause
        });
      }
      if (entry === this._entry) {
        this._debouncedCalculateState();
      }
    }
  }
}
FileItem.template = /* HTML */"\n  <div class=\"inner\" set=\"@finished: isFinished; @uploading: isUploading; @failed: isFailed; @focused: isFocused\">\n    <div class=\"thumb\" set=\"style.backgroundImage: thumbUrl\">\n      <div class=\"badge\">\n        <lr-icon set=\"@name: badgeIcon\"></lr-icon>\n      </div>\n    </div>\n    <div class=\"file-name-wrapper\">\n      <span class=\"file-name\" set=\"@title: itemName\">{{itemName}}</span>\n      <span class=\"file-error\" set=\"@hidden: !errorText\">{{errorText}}</span>\n    </div>\n    <div class=\"file-actions\">\n      <button type=\"button\" class=\"edit-btn mini-btn\" set=\"onclick: onEdit; @hidden: !isEditable\">\n        <lr-icon name=\"edit-file\"></lr-icon>\n      </button>\n      <button type=\"button\" class=\"remove-btn mini-btn\" set=\"onclick: onRemove;\">\n        <lr-icon name=\"remove-file\"></lr-icon>\n      </button>\n      <button type=\"button\" class=\"upload-btn mini-btn\" set=\"onclick: onUpload;\">\n        <lr-icon name=\"upload\"></lr-icon>\n      </button>\n    </div>\n    <lr-progress-bar class=\"progress-bar\" set=\"value: progressValue; visible: progressVisible;\"> </lr-progress-bar>\n  </div>\n";
FileItem.activeInstances = new Set();